import React, { useEffect, useState } from 'react'
import { Backdrop, Button, FormFieldSet, LogoLoader, Modal, showToast } from '../../../../components'
import { useSelector } from 'react-redux';
import { getApplicantsFromInternalDB } from '../../../../redux/slices/internalDbES/internalDbESApi';
import { fetchAllJobsApi } from '../../../../redux/slices/bdmjobs/bdmjobsApi';
import { addAiCallApi } from '../../../../redux/slices/aicalls/aicallsApi';
import { getAllIntegrationAccess, getAllIntegrations } from '../../../../redux/slices/setting/settingApi';

const AiCallModel = ({ isOpen, onClose, searchQuery, source, selectedApplicants }) => {
    const { totalApplicantsCount } = useSelector((state) => state.internalDbES);
    const { user } = useSelector((state) => state.auth);

    const [selectedJob, setSeletedJob] = useState();
    const [selectedJobDetails, setSeletedJobDetails] = useState();
    const [aiCallPage, setAiCallPage] = useState(1);
    const [aiCallLimit, setAiCallLimit] = useState(25);
    const [selectedAssistants, setSelectedAssistants] = useState();
    const [loading, setLoading] = useState(false);
    const [applicantsData, setApplicantsData] = useState([]);
    const [isNextClicked, setIsNextClicked] = useState(false);
    const [integrationAccessData, setIntegrationAccessData] = useState();
    const [assistantOptions, setAssistantOptions] = useState([]);
    const [hasError, setHasError] = useState(false);
    const [errorMsg, setErrMsg] = useState('');

    const fetchApplicants = async () => {
        if (!selectedJob || !selectedAssistants) {
            showToast("Please Fill the Details", "warning", 2000)
            return
        }
        const payload = {
            query: { ...searchQuery?.payload?.query },
            lim: Number(aiCallLimit) ?? 25,
            page: aiCallPage ?? 1,
            addedBy: user?.data?.uiid,
            source: 1,
            notes: false
        };

        try {
            setLoading(true);
            let data;
            if (source === 1) {
                if (selectedApplicants && selectedApplicants?.length > 0) {
                    data = selectedApplicants
                } else {
                    let res = await getApplicantsFromInternalDB({ payload: payload });
                    data = res?.data?.field?.data?.candidates
                }                
            } else if (source === 2 || source === 3) {
                data = selectedApplicants;
            }
            setApplicantsData(data)
                
            const fetchJob = await fetchAllJobsApi({
                coll: "JobPosting",
                fil: { uiid: {type: "NONE", value: selectedJob?.value}},
                ref: "Job"
            })

            if (fetchJob?.success) {
                setSeletedJobDetails(fetchJob?.data?.data);
            }

            const findSelectedAccess = integrationAccessData?.filter(val => val.integrationId.uiid === selectedAssistants);
            const credits = findSelectedAccess?.[0]?.accessConfig?.credits || 0;

            if (credits === 0) {
                showToast("Your Credits Expired", "error", 2000);
                setHasError(true);
                setErrMsg("");
            } else if (credits < data?.length) {
                setHasError(true);
                setErrMsg(`You only has ${credits} credits. You can initiate maximum of ${credits} calls`);
            } else {
                setHasError(false);
                setErrMsg("");
            }

            setIsNextClicked(true);
        } catch (err) {
            setLoading(false)
            console.error(err);
            showToast("Failed to fetch applicant details", "error");
        } finally {
            setLoading(false)
        }
    };

    const submitAiCalls = async () => {
        if (!applicantsData || applicantsData?.length === 0 || !selectedJob || !selectedAssistants) {
            showToast("Missing required information", "error", 2000);
            return;
        }

        try {
            setLoading(true);

            const seenCandidates = new Set();

            const buildPayload = applicantsData
                .map((applicant) => {
                    const candidateId = applicant?.uiid ?? applicant?.applicantId;
                    if (!candidateId || seenCandidates.has(candidateId)) {
                        return null;
                    }
                    seenCandidates.add(candidateId);
                    return {
                        candidateId,
                        addedBy: user.data.uiid,
                        integrationId: selectedAssistants,
                        jobPostId: selectedJob.value,
                        source
                    };
                })
                .filter(Boolean);

            if (!buildPayload.length) {
                showToast("No valid applicants found", "error", 2000);
                return;
            }

            const findSelectedAccess = integrationAccessData?.filter(val => val.integrationId.uiid === selectedAssistants);
            const accessId = findSelectedAccess?.[0]?.uiid;

            const aiCallResponse = await addAiCallApi({
                coll: "AiCall",
                bulkPost: true,
                addedBy: user?.data?.uiid,
                accessId,
                payload: buildPayload
            });

            if (aiCallResponse?.success) {
                showToast("AI Calls initiated successfully", "success", 2000);
                fetchIntegrations();
                handleCloseModel();
            } else if (aiCallResponse?.error) {
                showToast(aiCallResponse?.error, "error", 2000);
            } else {
                showToast("Failed to initiate AI calls", "error", 2000);
            }
        } catch (error) {
            console.error("Error initiating AI calls: ", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModel = () => {
        setSeletedJob();
        setAiCallPage(1);
        setAiCallLimit(25);
        setSelectedAssistants();
        setApplicantsData([]);
        setIsNextClicked(false);
        setHasError(false);
        setErrMsg("");
        onClose();
    };

    const fetchIntegrations = async () => {
        const integrationsPayload = {
          coll: "Integration",
          fil: JSON.stringify({category: {type: "NONE", value: 6}, type: {type: "NONE", value: 2}, status: {type: "NONE", value: 1}}),
          ret: "uiid name"
        }
        const getIntegrations = await getAllIntegrations(integrationsPayload);
    
        if (getIntegrations?.success) {
          const integrationIds = getIntegrations?.data?.data?.map((val) => val.uiid);
          const integrationAccessPayload = {
            coll: "IntegrationAccess",
            fil: {integrationId: {type: "IN", value: integrationIds}, userId: {type: "IN", value: user?.data?.uiid}, status: {type: "IN", value: 1}},
            ref: "Integration",
            incl: "uiid name"
          }
          const getIntAccess = await getAllIntegrationAccess(integrationAccessPayload);

          if (getIntAccess?.success) {
            const data = getIntAccess?.data?.data;
            setIntegrationAccessData(data);

            if (data && data?.length > 0) {
                const options = data?.map((access) => ({
                   label: access?.integrationId?.name, value: access?.integrationId?.uiid
                }));
                setAssistantOptions(options);
            }
          } 
        }
    };
    
    useEffect(() => {
        fetchIntegrations();
    }, []);

  return (
    <>
        <Modal
            isOpen={isOpen}
            title={"AI Call"}
            onClose={() => handleCloseModel()}
            titleColor="primary"
            width={"max-w-4xl"}
        >
            {isNextClicked ? (
                <div className='flex flex-col gap-4'>
                    <span>Initiating AI calls for <span className='text-primary font-semibold'>{applicantsData?.length}</span> Applicants.</span>
                    <p><span className='font-semibold'>Job Title: </span>{selectedJobDetails?.[0]?.jobId?.title}</p>
                    <p><span className='font-semibold'>Job Location: </span>{selectedJobDetails?.[0]?.jobInfo?.location?.city || ""}, {selectedJobDetails?.[0]?.jobInfo?.location?.state || ""}</p>
                    <p><span className='font-semibold'>Company Name: </span>{selectedJobDetails?.[0]?.jobId?.companyId?.name}</p>
                    {hasError && errorMsg?.length > 0 && (
                        <p className='self-center text-red-500 italic'>{errorMsg}</p>
                    )}
                    <div className="flex justify-center gap-4">
                        <Button
                            title="Cancel"
                            variant="outlined"
                            onClick={() => setIsNextClicked(false)}
                        />
                        <Button title="Submit" disabled={hasError} onClick={() => {submitAiCalls()}} />
                    </div>
                </div>
            ) : (
                <div className='flex flex-col gap-4'>
                    {selectedApplicants && selectedApplicants?.length > 0 && (
                        <span>Initiating AI calls for <span className='text-primary font-semibold'>{selectedApplicants?.length}</span> Applicants.</span>
                    )}
                    <FormFieldSet
                        type="companiesSearch"
                        title="Select The Job"
                        placeholder="Select The Job"
                        value={selectedJob}
                        showSearch
                        onChange={(value) => setSeletedJob(value)}
                    />
                    {source === 1 && !selectedApplicants?.length > 0 && (
                        <div className="flex items-center gap-2">
                            <FormFieldSet
                                type="text"
                                title="Enter no. range (From To)"
                                value={aiCallPage}
                                onChange={(value) => {
                                    const numericValue = Number(value);
                                    if (!/^\d+$/.test(numericValue)) return;
                                    if (numericValue <= 0) {
                                        setAiCallPage();
                                        showToast("Value should be at least 1", "warning", 2000);
                                    } else if (numericValue > totalApplicantsCount) {
                                        setAiCallPage(totalApplicantsCount);
                                        showToast(`Not more than Applicant Count ${totalApplicantsCount}`, "error", 2000);
                                    } else {
                                        setAiCallPage(numericValue);
                                    }
                                }}
                                placeholder="From"
                            />
                            <FormFieldSet
                                type="text"
                                title=" "
                                value={aiCallLimit}
                                onChange={(value) => {
                                    const numericValue = Number(value);
                                    if (numericValue <= 100) {
                                        setAiCallLimit(value);
                                    } else {
                                        setAiCallLimit(25);
                                        showToast("1000 is Max range", "warning", 1000)
                                    }
                                }}
                                placeholder="To"
                            />
                        </div>
                    )}                    
                    <FormFieldSet
                        type="select"
                        title="Choose Assistant"
                        placeholder="Choose Assistant"
                        shadow
                        options={assistantOptions}
                        value={selectedAssistants}
                        onChange={(value) => setSelectedAssistants(value)}
                    />
                    <div className="flex justify-end gap-4">
                        <Button
                            title="Cancel"
                            variant="outlined"
                            onClick={() => handleCloseModel()}
                        />
                        <Button title="Next" onClick={() => {fetchApplicants()}} />
                    </div>
                </div>
            )}
            
        </Modal>

        <Backdrop isOpen={loading} zIndex={53}>
            <LogoLoader />
        </Backdrop>
    </>
  )
}

export default AiCallModel